/**
 * useSmartRecommendations Hook
 * 
 * Phase 0-4 Learning Center recommendations with guardrails.
 * Supports shift-aware work hours using technician shift and shop timezone.
 * Phase 2: Integrates upcoming offerings into scoring.
 * Phase 3: Integrates effectiveness signals into scoring.
 */

import { useArtifactsWithProviders } from './useTrainingProviders';
import { useLearningHistory } from './useLearningCenter';
import { useUpcomingOfferings } from './useOfferingInstances';
import { usePositiveEffectSignals } from './useLearningImpactLoop';
import {
  scoreArtifacts,
  type ScoredArtifact
} from '@/utils/trainingRecommendationEngine';
// Batch 18-D9: source applyRecommendationCaps from its canonical module
// (extracted in 18-D2) instead of via the engine's back-compat re-export.
// The engine still re-exports it for any remaining external consumer;
// the production consumer now points at the canonical path.
import { applyRecommendationCaps } from '@/utils/training/guardrails/caps';
import { toZonedTime } from 'date-fns-tz';
// DTC-E: route the work-hours "now" anchor through the canonical
// clock so the shift-aware path is deterministic under
// `clock.setFixed(...)`. The `zonedNow.getHours()` read is class-C
// safe (operates on a pre-zoned Date from `toZonedTime`); the L61
// fallback `now.getHours()` is intentional browser-local heuristic
// per its inline comment ("simple 7 AM - 6 PM local time heuristic")
// and stays unchanged.
import { clock } from '@/utils/clock';

interface TechShift {
  start: string; // HH:mm format, e.g. "07:00"
  end: string;   // HH:mm format, e.g. "17:00"
}

interface UseSmartRecommendationsOptions {
  technicianProfileId: string;
  targetDomains?: string[];
  isWorkHours?: boolean;
  managerOverride?: boolean;
  limit?: number;
  techShift?: TechShift;
  shopTimezone?: string;
  viewerRole?: 'technician' | 'manager' | 'admin';
}

/**
 * Determine if current time is within work hours.
 * Uses technician shift + shop timezone if provided, otherwise falls back to 7-18 local heuristic.
 */
function calculateIsWorkHours(techShift?: TechShift, shopTimezone?: string): boolean {
  try {
    const now = clock.now();
    
    // If we have shift + timezone, use them
    if (techShift && shopTimezone) {
      const zonedNow = toZonedTime(now, shopTimezone);
      const currentHour = zonedNow.getHours();
      const currentMinute = zonedNow.getMinutes();
      const currentTimeMinutes = currentHour * 60 + currentMinute;
      
      const [startH, startM] = techShift.start.split(':').map(Number);
      const [endH, endM] = techShift.end.split(':').map(Number);
      const shiftStartMinutes = startH * 60 + startM;
      const shiftEndMinutes = endH * 60 + endM;
      
      return currentTimeMinutes >= shiftStartMinutes && currentTimeMinutes < shiftEndMinutes;
    }
    
    // Fallback: simple 7 AM - 6 PM local time heuristic
    const hour = now.getHours();
    return hour >= 7 && hour < 18;
  } catch {
    // On any error, default to work hours
    return true;
  }
}

export function useSmartRecommendations(options: UseSmartRecommendationsOptions) {
  const {
    technicianProfileId,
    targetDomains = [],
    isWorkHours: isWorkHoursOverride,
    managerOverride = false,
    limit = 40,
    techShift,
    shopTimezone,
    viewerRole = 'technician',
  } = options;

  // Calculate work hours: explicit override > shift-aware > heuristic
  const isWorkHours = isWorkHoursOverride ?? calculateIsWorkHours(techShift, shopTimezone);

  const { 
    data: artifacts, 
    isLoading: loadingArtifacts,
    error: artifactsError 
  } = useArtifactsWithProviders({ 
    domains: targetDomains.length > 0 ? targetDomains : undefined,
    limit 
  });

  const { data: history, isLoading: loadingHistory } = useLearningHistory(technicianProfileId);

  // Phase 2: Fetch upcoming offerings for all artifacts
  const artifactIds = artifacts?.map(a => a.id) || [];
  const { data: upcomingOfferings } = useUpcomingOfferings(artifactIds, {
    withinDays: 60,
    timezone: shopTimezone,
  });

  // Phase 3: Fetch positive effect signals for artifacts
  const { data: effectSignals } = usePositiveEffectSignals(artifactIds);

  const isLoading = loadingArtifacts || loadingHistory;

  const recommendations: ScoredArtifact[] = (() => {
    if (!artifacts || artifacts.length === 0) return [];

    // Phase 2: Merge offerings into artifacts
    const offeringsByArtifact = new Map<string, typeof upcomingOfferings>();
    if (upcomingOfferings) {
      for (const offering of upcomingOfferings) {
        const existing = offeringsByArtifact.get(offering.artifact_id) || [];
        existing.push(offering);
        offeringsByArtifact.set(offering.artifact_id, existing);
      }
    }

    // Phase 3: Build effect signals map (artifact_id -> weight)
    const effectSignalsMap: Record<string, number> = {};
    if (effectSignals) {
      for (const signal of effectSignals) {
        // Use confidence_weight as the effect weight (already capped at source)
        const currentWeight = effectSignalsMap[signal.artifact_id] || 0;
        // Take the max positive signal for this artifact
        effectSignalsMap[signal.artifact_id] = Math.max(currentWeight, signal.confidence_weight * 0.3);
      }
    }

    const artifactsWithDomains = artifacts.map(a => ({
      ...a,
      artifact_type: a.artifact_type as any,
      access_type: a.access_type as any,
      skill_depth: a.skill_depth as any,
      delivery_format: a.delivery_format as any,
      artifact_scope: a.artifact_scope as any,
      confidence_level: a.confidence_level as any,
      // Phase 2: Attach upcoming offerings
      upcomingOfferings: offeringsByArtifact.get(a.id)?.map(o => ({
        id: o.id,
        start_datetime: o.start_datetime,
        status: o.status,
        registration_url: o.registration_url,
      })),
    }));

    const scored = scoreArtifacts({
      artifacts: artifactsWithDomains,
      history: history?.map(h => ({
        ...h,
        source: h.source as any,
        intent: h.intent as any,
      })) || [],
      targetDomains,
      isWorkHours,
      managerOverride,
      viewerRole,
      effectSignals: effectSignalsMap, // Phase 3: Pass effect signals
    });

    return applyRecommendationCaps(scored, { managerOverride });
  })();

  return {
    recommendations,
    isLoading,
    error: artifactsError,
    meta: {
      totalCandidates: artifacts?.length || 0,
      afterCaps: recommendations.length,
      isWorkHours,
      managerOverride,
      shiftAware: !!(techShift && shopTimezone),
      hasOfferings: (upcomingOfferings?.length || 0) > 0,
      hasEffectSignals: Object.keys(effectSignals || {}).length > 0,
    },
  };
}
